/**
 * WeaveIn.ai Platform - Pricing Negotiation Agent API Service
 * TypeScript service layer for AI-powered pricing negotiations
 * Integrates with the Pricing Negotiation Agent backend service
 */

import { apiClient } from '../apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type AgentState =
  | 'idle'
  | 'gathering_context'
  | 'generating_proposal'
  | 'sending_proposal'
  | 'awaiting_response'
  | 'evaluating_counter'
  | 'preparing_counter'
  | 'sending_counter'
  | 'finalizing_agreement'
  | 'escalated'
  | 'completed'
  | 'failed';

export type TriggerType =
  | 'workflow_event'
  | 'user_request'
  | 'schedule'
  | 'provider_response'
  | 'timeout';

export type CommunicationChannel = 'email' | 'sms' | 'voice' | 'portal';

export type CounterDecision = 'accept' | 'counter' | 'escalate' | 'reject' | 'wait';

export interface PricingIntelligence {
  service_type_code: string;
  service_type_name: string;
  provider_id: string;
  provider_type: string;
  urgency_level: string;
  state_code?: string;

  // Market data
  market_average: number;
  market_low: number;
  market_high: number;
  market_data_points: number;

  // ML recommendation
  ml_recommended_rate: number;
  ml_confidence: number;

  // Provider data
  provider_rate_card?: number;
  provider_historical_acceptance_rate: number;
  provider_avg_negotiation_rounds: number;

  // Contract terms
  contract_payment_terms_days: number;

  // Negotiation boundaries
  opening_price: number;
  target_price: number;
  walkaway_price: number;
  counter_increment: number;
}

export interface AgentSession {
  id: string;
  service_referral_id: number;
  referral_number?: string;
  provider_id: string;
  provider_type: string;
  provider_name?: string;
  state: AgentState;
  trigger_type: TriggerType;

  // Negotiation context
  current_round: number;
  max_rounds: number;
  our_last_offer?: number;
  their_last_offer?: number;
  channel?: CommunicationChannel;

  // Timing
  created_at: string;
  updated_at: string;
  last_activity_at?: string;
  expires_at?: string;

  // Result
  final_amount?: number;
  final_decision?: CounterDecision;
  escalation_reason?: string;

  // Metadata
  intelligence?: PricingIntelligence;
}

export interface AgentDecision {
  id: number;
  session_id: string;
  round_number: number;
  decision_type: CounterDecision;
  our_offer?: number;
  their_offer?: number;
  confidence: number;
  reasoning: string;
  message_sent?: string;
  ai_model?: string;
  input_tokens?: number;
  output_tokens?: number;
  latency_ms?: number;
  created_at: string;
}

export interface NegotiationHistoryItem {
  round: number;
  actor: 'agent' | 'provider';
  action: string;
  amount?: number;
  message?: string;
  channel?: CommunicationChannel;
  timestamp: string;
}

export interface AgentMetrics {
  total_sessions: number;
  active_sessions: number;
  completed_sessions: number;
  success_rate: number;
  avg_rounds_to_agreement: number;
  avg_savings_percentage: number;
  escalation_rate: number;
  by_channel: Record<CommunicationChannel, {
    total: number;
    success_rate: number;
    avg_response_time_hours: number;
  }>;
}

// Request types
export interface InitiateAgentRequest {
  referral_id: number;
  provider_type: string;
  provider_id: string | number;
  channel?: CommunicationChannel;
  max_rounds?: number;
  strategy?: 'aggressive' | 'balanced' | 'accommodating' | 'urgent';
}

export interface AutoInitiateRequest {
  referral_id: number;
  event_type: string;
  event_data?: Record<string, any>;
}

export interface ProviderResponseRequest {
  session_id: string;
  response_type: 'accept' | 'counter' | 'decline' | 'info_request';
  counter_amount?: number;
  justification?: string;
  message?: string;
}

export interface EscalateRequest {
  reason: string;
  notes?: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class PricingAgentAPI {
  // ========================================================================
  // SESSION MANAGEMENT
  // ========================================================================

  /**
   * Manually initiate a pricing negotiation agent session
   */
  async initiateSession(data: InitiateAgentRequest): Promise<AgentSession> {
    const response = await apiClient.post<AgentSession>(
      '/api/pricing-agent/initiate',
      data
    );
    return response.data;
  }

  /**
   * Auto-initiate from workflow event
   */
  async autoInitiate(data: AutoInitiateRequest): Promise<AgentSession> {
    const response = await apiClient.post<AgentSession>(
      '/api/pricing-agent/initiate-auto',
      data
    );
    return response.data;
  }

  /**
   * Get session by ID
   */
  async getSession(sessionId: string): Promise<AgentSession> {
    const response = await apiClient.get<AgentSession>(
      `/api/pricing-agent/session/${sessionId}`
    );
    return response.data;
  }

  /**
   * Get all sessions for a referral
   */
  async getSessionsForReferral(referralId: number): Promise<AgentSession[]> {
    const response = await apiClient.get<AgentSession[]>(
      `/api/pricing-agent/referral/${referralId}/sessions`
    );
    return response.data;
  }

  /**
   * Get all active sessions
   */
  async getActiveSessions(): Promise<AgentSession[]> {
    const response = await apiClient.get<AgentSession[]>(
      '/api/pricing-agent/active'
    );
    return response.data;
  }

  /**
   * Cancel/terminate a session
   */
  async cancelSession(sessionId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      `/api/pricing-agent/session/${sessionId}`
    );
    return response.data;
  }

  // ========================================================================
  // NEGOTIATION FLOW
  // ========================================================================

  /**
   * Submit a provider response (accept, counter, decline)
   */
  async submitProviderResponse(data: ProviderResponseRequest): Promise<AgentSession> {
    const response = await apiClient.post<AgentSession>(
      '/api/pricing-agent/response',
      data
    );
    return response.data;
  }

  /**
   * Manually escalate a session to human review
   */
  async escalateSession(sessionId: string, data: EscalateRequest): Promise<AgentSession> {
    const response = await apiClient.post<AgentSession>(
      `/api/pricing-agent/session/${sessionId}/escalate`,
      data
    );
    return response.data;
  }

  // ========================================================================
  // HISTORY & INTELLIGENCE
  // ========================================================================

  /**
   * Get negotiation history for a referral
   */
  async getNegotiationHistory(referralId: number): Promise<NegotiationHistoryItem[]> {
    const response = await apiClient.get<NegotiationHistoryItem[]>(
      `/api/pricing-agent/referral/${referralId}/history`
    );
    return response.data;
  }

  /**
   * Get pricing intelligence preview (without starting negotiation)
   */
  async getIntelligencePreview(
    referralId: number,
    providerId: string | number
  ): Promise<PricingIntelligence> {
    const response = await apiClient.get<PricingIntelligence>(
      `/api/pricing-agent/intelligence/${referralId}/${providerId}`
    );
    return response.data;
  }

  /**
   * Get agent decisions for a session
   */
  async getSessionDecisions(sessionId: string): Promise<AgentDecision[]> {
    const response = await apiClient.get<AgentDecision[]>(
      `/api/pricing-agent/session/${sessionId}/decisions`
    );
    return response.data;
  }

  // ========================================================================
  // METRICS
  // ========================================================================

  /**
   * Get aggregated agent metrics
   */
  async getMetrics(params?: {
    days?: number;
    provider_type?: string;
  }): Promise<AgentMetrics> {
    const response = await apiClient.get<AgentMetrics>(
      '/api/pricing-agent/metrics',
      { params }
    );
    return response.data;
  }
}

// Export singleton instance
export const pricingAgentAPI = new PricingAgentAPI();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getAgentStateLabel(state: AgentState): string {
  const labels: Record<AgentState, string> = {
    idle: 'Idle',
    gathering_context: 'Gathering Context',
    generating_proposal: 'Generating Proposal',
    sending_proposal: 'Sending Proposal',
    awaiting_response: 'Awaiting Response',
    evaluating_counter: 'Evaluating Counter',
    preparing_counter: 'Preparing Counter',
    sending_counter: 'Sending Counter',
    finalizing_agreement: 'Finalizing Agreement',
    escalated: 'Escalated',
    completed: 'Completed',
    failed: 'Failed',
  };
  return labels[state] || state;
}

export function getAgentStateColor(state: AgentState): string {
  const colors: Record<AgentState, string> = {
    idle: 'gray',
    gathering_context: 'blue',
    generating_proposal: 'blue',
    sending_proposal: 'blue',
    awaiting_response: 'yellow',
    evaluating_counter: 'purple',
    preparing_counter: 'purple',
    sending_counter: 'blue',
    finalizing_agreement: 'green',
    escalated: 'orange',
    completed: 'green',
    failed: 'red',
  };
  return colors[state] || 'gray';
}

export function getChannelLabel(channel: CommunicationChannel): string {
  const labels: Record<CommunicationChannel, string> = {
    email: 'Email',
    sms: 'SMS',
    voice: 'Voice AI',
    portal: 'Portal',
  };
  return labels[channel] || channel;
}

export function getChannelIcon(channel: CommunicationChannel): string {
  const icons: Record<CommunicationChannel, string> = {
    email: 'Mail',
    sms: 'MessageSquare',
    voice: 'Phone',
    portal: 'Globe',
  };
  return icons[channel] || 'MessageCircle';
}

export function getDecisionLabel(decision: CounterDecision): string {
  const labels: Record<CounterDecision, string> = {
    accept: 'Accepted',
    counter: 'Counter Proposed',
    escalate: 'Escalated',
    reject: 'Rejected',
    wait: 'Waiting',
  };
  return labels[decision] || decision;
}

export function getDecisionColor(decision: CounterDecision): string {
  const colors: Record<CounterDecision, string> = {
    accept: 'green',
    counter: 'blue',
    escalate: 'orange',
    reject: 'red',
    wait: 'gray',
  };
  return colors[decision] || 'gray';
}

export function isAgentActive(state: AgentState): boolean {
  return !['idle', 'completed', 'failed', 'escalated'].includes(state);
}

export function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default pricingAgentAPI;
